/**
 * Email History Date Utilities
 * Shared date formatting and conversion utilities
 */

/**
 * Convert ISO date string to YYYY-MM-DD format for date input
 * @param {string} isoString - ISO date string
 * @returns {string} YYYY-MM-DD format string or empty string
 */
export function isoToDateInput(isoString) {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toISOString().split('T')[0];
  } catch (error) {
    return '';
  }
}

/**
 * Format date for display (short format: "Oct 6, 2025")
 * @param {string} dateString - Date string
 * @returns {string} Formatted date string or 'N/A'
 */
export function formatDate(dateString) {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return dateString;
  }
}

/**
 * Format date/time for display (full format: "Oct 6, 2025 10:10 AM")
 * @param {string} dateString - Date string
 * @returns {string} Formatted date/time string or 'N/A'
 */
export function formatDateTime(dateString) {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    return dateString;
  }
}

/**
 * Format date with time (includes hours and minutes)
 * @param {string} dateString - Date string
 * @returns {string} Formatted date/time string or 'N/A'
 */
export function formatDateWithTime(dateString) {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return dateString;
  }
}

/**
 * Get default date range (last 30 days)
 * @returns {{from: string, to: string}} Date range in YYYY-MM-DD format
 */
export function getDefaultDateRange() {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);
  
  return {
    from: thirtyDaysAgo.toISOString().split('T')[0],
    to: today.toISOString().split('T')[0],
  };
}

/**
 * Convert date range from YYYY-MM-DD to ISO strings for API
 * @param {{from: string, to: string}} dateRange - Date range in YYYY-MM-DD format
 * @returns {{dateFrom: string|null, dateTo: string|null}} ISO date strings
 */
export function dateRangeToISO(dateRange) {
  if (!dateRange) {
    return { dateFrom: null, dateTo: null };
  }

  const dateFrom = dateRange.from
    ? new Date(dateRange.from + 'T00:00:00').toISOString()
    : null;
  const dateTo = dateRange.to
    ? new Date(dateRange.to + 'T23:59:59').toISOString()
    : null;

  return { dateFrom, dateTo };
}

